import React, { useEffect, useRef } from "react";
import { Calendar, CalendarOptions } from "@fullcalendar/core";
import listPlugin from "@fullcalendar/list";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin, { DateClickArg } from "@fullcalendar/interaction";
import { Doctor, Event } from "@/lib/types/models.ts";
import { usePage } from "@inertiajs/react";
import hrLocale from "@fullcalendar/core/locales/hr";
import { Sun } from "lucide-react";
import { createRoot } from "react-dom/client";
import { mapAppEventToFullCalendarEvent } from "@/lib/mapUtils.ts";
import { router } from "@inertiajs/core";

interface MobileAppointmentCalendarProps {
    events: Event[];
    onDateClick?: (start_at: string) => void;
    onEventClick?: (event: Event) => void;
    doctors: Doctor[];
    calendarRefCallback?: (calendar: Calendar) => void;
}

const dayShortLower = ["ned", "pon", "uto", "sre", "čet", "pet", "sub"];

const MobileAppointmentCalendar: React.FC<MobileAppointmentCalendarProps> = ({
                                                                           events,
                                                                           onDateClick,
                                                                           onEventClick,
                                                                           doctors,
                                                                           calendarRefCallback,
                                                                       }) => {
    const calendarRef = useRef<HTMLDivElement | null>(null);
    const calendarInstanceRef = useRef<Calendar | null>(null);
    const { translations } = usePage().props as unknown as { translations: any };
    const t = translations.calendar;

    useEffect(() => {
        const styleId = "mobile-calendar-compact-styles";
        if (document.getElementById(styleId)) return;
        const style = document.createElement("style");
        style.id = styleId;
        style.innerHTML = `
      .fc { font-size: 13px; }
      .fc .fc-toolbar { padding: 6px 8px; }
      .fc .fc-toolbar-chunk { gap: 6px; }
      .fc .fc-button { padding: 4px 6px; font-size: 12px; border-radius: 6px; min-width: 34px; }
      .fc .fc-button.fc-button-primary { padding: 4px 8px; }
      .fc .fc-toolbar-title { font-size: 14px; padding: 0 6px; }
      .fc .fc-list-item { padding-top: 6px; padding-bottom: 6px; }
      .fc .fc-list-event .fc-list-item-title { font-size: 13px; }
      .fc .fc-list-event .fc-list-item-time { font-size: 12px; opacity: 0.9; }
      .mobile-calendar-container { padding: 4px; }
    `;
        document.head.appendChild(style);
        return () => style.remove();
    }, []);

    useEffect(() => {
        if (!calendarRef.current) return;

        const savedDate = localStorage.getItem("lastCalendarDate");
        const savedView = localStorage.getItem("lastCalendarView");
        const initialDate = savedDate ? new Date(savedDate) : undefined;

        const options: CalendarOptions = {
            plugins: [listPlugin, dayGridPlugin, interactionPlugin],
            initialView: (savedView as string) || "listWeek",
            initialDate,
            displayEventTime: false,
            hiddenDays: [0, 6],
            headerToolbar: {
                left: "prev,next,todayIcon",
                center: "title",
                right: "dayGridMonth,listWeek,listDay",
            },
            customButtons: {
                todayIcon: {
                    text: "",
                    click: () => calendarInstanceRef.current?.today(),
                },
            },
            buttonText: {
                dayGridMonth: t.mobile.views.month,
                listWeek: t.mobile.views.week,
                listDay: t.mobile.views.day,
            },
            locale: hrLocale,
            selectable: true,
            editable: true,
            eventStartEditable: true,
            eventDurationEditable: true,
            snapDuration: "00:15:00",
            events: events.map((e) => mapAppEventToFullCalendarEvent(e, doctors)),
            dateClick: (info: DateClickArg) => onDateClick?.(info.dateStr),
            eventClick: (info) => {
                const clickedEvent = events.find((e) => e.id.toString() === info.event.id);
                if (clickedEvent) onEventClick?.(clickedEvent);
            },
            eventDrop: (info) => {
                const id = info.event.id;
                const start = info.event.start?.toISOString();
                const end = info.event.end?.toISOString() ?? null;
                if (!start) return info.revert();
                router.patch(`/events/${id}`, { event: { start_at: start, end_at: end } }, { preserveScroll: true, onError: () => info.revert() });
            },
            eventResize: (info) => {
                const id = info.event.id;
                const start = info.event.start?.toISOString();
                const end = info.event.end?.toISOString();
                if (!start || !end) return info.revert();
                router.patch(`/events/${id}`, { event: { start_at: start, end_at: end } }, { preserveScroll: true, onError: () => info.revert() });
            },
            eventDidMount: (info) => {
                if (info.event.start && info.event.end) {
                    const startTime = info.event.start.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
                    const endTime = info.event.end.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
                    info.el.title = `${startTime} - ${endTime}`;
                }
            },
            views: {
                dayGridMonth: { displayEventTime: false, titleFormat: { month: "numeric", year: "numeric" } },
                listWeek: { displayEventTime: true, titleFormat: { day: "numeric", month: "numeric" } },
                listDay: { displayEventTime: true, titleFormat: { day: "numeric", month: "numeric", year: "numeric" } },
            },
            dayHeaderContent: (args) => {
                const dayIndex = args.date.getDay();
                if (args.view.type === "dayGridMonth") return dayShortLower[dayIndex];
                const dateNum = args.date.getDate();
                const monthNum = args.date.getMonth() + 1;
                return `${dayShortLower[dayIndex]} ${dateNum.toString().padStart(2, "0")}.${monthNum.toString().padStart(2, "0")}`;
            },
            datesSet: (info) => {
                localStorage.setItem("lastCalendarDate", info.start.toISOString());
                localStorage.setItem("lastCalendarView", info.view.type);
            },
        };

        const calendar = new Calendar(calendarRef.current, options);
        calendar.render();
        setTimeout(() => {
            const btn = document.querySelector(".fc-todayIcon-button");
            if (btn) {
                btn.innerHTML = "";
                const root = createRoot(btn as HTMLElement);
                root.render(<Sun size={16} strokeWidth={2} />);
            }
        }, 0);
        calendarInstanceRef.current = calendar;
        calendarRefCallback?.(calendar);

        return () => calendar.destroy();
    }, [events, onDateClick, onEventClick, t, doctors, calendarRefCallback]);

    return <div className="mobile-calendar-container w-full h-full rounded-lg" ref={calendarRef} />;
};

export default MobileAppointmentCalendar;